// ═══════════════════════════════════════════════════════════════════
//  modules/erp/PlayerSanctionsTab.js
//  L'onglet Sanctions d'une fiche joueur : le dossier d'UN joueur.
//  La vue d'ensemble est dans le module « Suivi des Sanctions » —
//  même table, deux entrées (CDC §5).
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { THEME } from "../../socle/theme";
import { sanctionsApi } from "../sanctions/api";
import { dateCourte, dureeLisible, NIVEAU_STYLE, STATUT_STYLE, tempsRestant } from "../sanctions/constantes";

export function PlayerSanctionsTab({ username, token, onNavigate }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    try { setD(await sanctionsApi(token).joueur(username)); setErreur(null); }
    catch (e) { setErreur(e.message); }
  }, [token, username]);
  useEffect(() => { charger(); }, [charger]);

  if (erreur) return <div style={{ color: THEME.red, padding: 20 }}>{erreur}</div>;
  if (!d) return <div style={{ color: THEME.textMuted, padding: 20 }}>Chargement…</div>;

  const carte = { background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 14, marginBottom: 12 };
  const lbl = { color: THEME.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 };

  return (
    <div>
      {d.actives.length > 0 && (
        <div style={{ ...carte, borderColor: THEME.red }}>
          <div style={{ ...lbl, color: THEME.red }}>Sanctions en cours</div>
          {d.actives.map((s) => {
            const nv = NIVEAU_STYLE[s.niveau] || NIVEAU_STYLE.kick;
            return (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", marginBottom: 6 }}>
                <span style={{ background: nv.bg, color: nv.color, borderRadius: 5, padding: "2px 9px", fontSize: 11, fontWeight: 800 }}>{nv.icone} {nv.label}</span>
                <span style={{ color: THEME.text, fontSize: 13 }}>{s.motif}</span>
                <span style={{ color: THEME.textMuted, fontSize: 12 }}>
                  {s.expire_le ? `encore ${tempsRestant(s.expire_le)}` : "définitif"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {d.comptes_lies.length > 0 && (
        <div style={{ ...carte, borderColor: THEME.emerald }}>
          <div style={{ ...lbl, color: THEME.emerald }}>Comptes liés autorisés</div>
          {d.comptes_lies.map((p) => (
            <div key={p.id} style={{ color: THEME.text, fontSize: 13 }}>
              {p.pseudo_a} ↔ {p.pseudo_b} <span style={{ color: THEME.textMuted }}>· {p.motif}</span>
            </div>
          ))}
        </div>
      )}

      {d.dossiers.length > 0 && (
        <div style={{ ...carte, borderColor: THEME.accent }}>
          <div style={{ ...lbl, color: THEME.accent }}>Dossiers double compte</div>
          {d.dossiers.map((x) => (
            <div key={x.id} style={{ color: THEME.text, fontSize: 13 }}>
              #{x.id} · {x.pseudos} · <span style={{ color: THEME.textMuted }}>{x.statut}</span>
            </div>
          ))}
        </div>
      )}

      <div style={carte}>
        <div style={lbl}>Compteurs par motif — prescription {d.prescription_jours} jours</div>
        {Object.keys(d.paliers).length === 0 && (
          <div style={{ color: THEME.textDim, fontSize: 13 }}>Aucun avertissement enregistré.</div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {Object.entries(d.paliers).map(([motif, p]) => (
            <span key={motif} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "3px 10px", fontSize: 12, color: THEME.text }}>
              {motif} : <b>{p.actifs}</b>
              {p.prescrits > 0 && <span style={{ color: THEME.textDim }}> (+{p.prescrits} prescrit{p.prescrits > 1 ? "s" : ""})</span>}
            </span>
          ))}
        </div>
      </div>

      <div style={carte}>
        <div style={lbl}>Historique complet</div>
        {d.sanctions.length === 0 && (
          <div style={{ color: THEME.textMuted, fontSize: 13, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: 30, marginBottom: 6 }}>✅</div>
            Aucune sanction enregistrée pour ce joueur.
          </div>
        )}
        {d.sanctions.map((s) => {
          const nv = NIVEAU_STYLE[s.niveau] || NIVEAU_STYLE.kick;
          const st = STATUT_STYLE[s.statut] || STATUT_STYLE.expiree;
          return (
            <div key={s.id}
              onClick={() => onNavigate && onNavigate("sanctions", { sanctionId: s.id })}
              style={{ borderLeft: `3px solid ${nv.color}`, background: THEME.surface, borderRadius: 8, padding: "9px 12px", marginBottom: 7, cursor: onNavigate ? "pointer" : "default" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ background: nv.bg, color: nv.color, borderRadius: 5, padding: "1px 8px", fontSize: 10.5, fontWeight: 800 }}>{nv.label}</span>
                <span style={{ color: THEME.text, fontSize: 13 }}>{s.motif}</span>
                <span style={{ color: THEME.textMuted, fontSize: 12 }}>{dureeLisible(s.duree_min)}</span>
                <span style={{ background: st.bg, color: st.color, borderRadius: 5, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{st.label}</span>
                <span style={{ marginLeft: "auto", color: THEME.textDim, fontSize: 11 }}>
                  {dateCourte(s.created_at)} · {s.moderateur_pseudo}
                </span>
              </div>
              {s.raison_publique && (
                <div style={{ color: THEME.textMuted, fontSize: 12.5, marginTop: 3, fontStyle: "italic" }}>« {s.raison_publique} »</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
